import React, { useState, useEffect } from 'react';
import { Spin, Typography, List, Tag, Tooltip, Card, Row, Col, Statistic } from 'antd';
import { LinkOutlined, RiseOutlined, FallOutlined, PartitionOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import api from '../functions/api';

const { Title, Text } = Typography;

const MarketChangesAndTrends = ({ tickerValue }) => {
  const [trendData, setTrendData] = useState([]);
  const [aggregateData, setAggregateData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aggregateLoading, setAggregateLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!tickerValue) return;
    
    const fetchTrendData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await api.get('/trendsearch_data', {
          params: { ticker: tickerValue }
        });
        
        if (response.data.success) {
          setTrendData(response.data.data);
        } else {
          setError('Failed to load trend data');
        }
      } catch (err) {
        console.error('Error fetching trend data:', err);
        setError('Error loading trend data');
      } finally {
        setLoading(false);
      }
    };

    const fetchAggregateData = async () => {
      setAggregateLoading(true);
      
      try {
        const response = await api.get('/trendsearch_ticker_aggregates', {
          params: { 
            ticker: tickerValue,
            limit: 1
          }
        });
        
        if (response.data.success && response.data.data.length > 0) {
          setAggregateData(response.data.data[0]);
        } else {
          setAggregateData(null);
        }
      } catch (err) {
        console.error('Error fetching aggregate data:', err);
        setAggregateData(null);
      } finally {
        setAggregateLoading(false);
      }
    };

    fetchTrendData();
    fetchAggregateData();
  }, [tickerValue]);

  const getImpactIcon = (impactType) => {
    switch (impactType) {
      case 'company_winner':
        return <RiseOutlined style={{ color: '#52c41a' }} />;
      case 'company_loser':
        return <FallOutlined style={{ color: '#ff4d4f' }} />;
      case 'company_enabler':
        return <PartitionOutlined style={{ color: '#1890ff' }} />;
      default:
        return null;
    }
  };

  const getImpactColor = (impactType) => {
    switch (impactType) {
      case 'company_winner':
        return '#52c41a';
      case 'company_loser':
        return '#ff4d4f';
      case 'company_enabler':
        return '#1890ff';
      default:
        return '#666';
    }
  };

  const formatImpactType = (impactType) => {
    switch (impactType) {
      case 'company_winner':
        return 'Winner';
      case 'company_loser':
        return 'Loser';
      case 'company_enabler':
        return 'Enabler';
      default:
        return impactType;
    }
  };

  const handleItemClick = (articleUrl) => {
    if (articleUrl) {
      window.open(articleUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const getSentimentIndicator = (sentiment) => {
    if (sentiment >= 5) return { icon: <ArrowUpOutlined />, color: '#52c41a', text: 'Very Positive' };
    if (sentiment >= 2) return { icon: <ArrowUpOutlined />, color: '#52c41a', text: 'Positive' };
    if (sentiment >= -2) return { icon: <ArrowUpOutlined />, color: '#faad14', text: 'Neutral' };
    if (sentiment >= -5) return { icon: <ArrowDownOutlined />, color: '#ff4d4f', text: 'Negative' };
    return { icon: <ArrowDownOutlined />, color: '#ff4d4f', text: 'Very Negative' };
  };

  if (!tickerValue) return null;

  return (
    <div style={{ marginTop: '20px', marginBottom: '20px' }}>
      <Title level={3} style={{ textAlign: 'center', marginBottom: '16px' }}>
        Market Changes and Trends
      </Title>
      
      {/* Company Aggregate Trend Summary */}
      <Spin spinning={aggregateLoading}>
        {aggregateData && (
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text strong>Overall Company Trend Summary</Text>
                <Tag color="blue">{aggregateData.ticker}</Tag>
              </div>
            }
            style={{ marginBottom: '20px' }}
            size="small"
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="Overall Sentiment"
                  value={parseFloat(aggregateData.overall_sentiment) || 0}
                  precision={2}
                  valueStyle={{ 
                    color: getSentimentIndicator(parseFloat(aggregateData.overall_sentiment) || 0).color,
                    fontSize: '20px',
                    fontWeight: 'bold'
                  }}
                  prefix={getSentimentIndicator(parseFloat(aggregateData.overall_sentiment) || 0).icon}
                  suffix={
                    <Text style={{ 
                      fontSize: '12px', 
                      color: getSentimentIndicator(parseFloat(aggregateData.overall_sentiment) || 0).color 
                    }}>
                      {getSentimentIndicator(parseFloat(aggregateData.overall_sentiment) || 0).text}
                    </Text>
                  }
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="Total Impacts"
                  value={aggregateData.total_impacts}
                  valueStyle={{ color: '#1890ff', fontSize: '20px', fontWeight: 'bold' }}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="Winners"
                  value={aggregateData.winner_count}
                  valueStyle={{ color: '#52c41a', fontSize: '20px', fontWeight: 'bold' }}
                  prefix={<RiseOutlined />}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="Losers"
                  value={aggregateData.loser_count}
                  valueStyle={{ color: '#ff4d4f', fontSize: '20px', fontWeight: 'bold' }}
                  prefix={<FallOutlined />}
                />
              </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="Enablers"
                  value={aggregateData.enabler_count}
                  valueStyle={{ color: '#1890ff', fontSize: '16px' }}
                  prefix={<PartitionOutlined />}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="Recent Activity (7d)"
                  value={aggregateData.recent_activity_7d}
                  valueStyle={{ color: '#faad14', fontSize: '16px' }}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="Sector"
                  value={aggregateData.sector}
                  valueStyle={{ color: '#666', fontSize: '16px' }}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="Industry"
                  value={aggregateData.industry}
                  valueStyle={{ color: '#666', fontSize: '16px' }}
                />
              </Col>
            </Row>
            <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
              <Text style={{ fontSize: '12px', color: '#666' }}>
                <strong>Impact Distribution:</strong> {aggregateData.impact_distribution} | 
                <strong> Timeline:</strong> {aggregateData.trend_timeline} | 
                <strong> Latest:</strong> {aggregateData.latest_impact_date}
              </Text>
            </div>
          </Card>
        )}
      </Spin>

      {/* Individual Article Results */}
      <Title level={4} style={{ marginBottom: '12px', color: '#666' }}>
        Individual Article Analysis
      </Title>
      
      <Spin spinning={loading}>
        {error ? (
          <div style={{ textAlign: 'center', color: '#ff4d4f', padding: '20px' }}>
            <Text type="secondary">{error}</Text>
          </div>
        ) : trendData.length === 0 && !loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Text type="secondary">No individual trend data available for {tickerValue}</Text>
          </div>
        ) : (
          <List
            dataSource={trendData}
            renderItem={(item) => (
              <List.Item
                style={{
                  cursor: item.article_url ? 'pointer' : 'default',
                  padding: '12px 16px',
                  border: '1px solid #f0f0f0',
                  borderRadius: '6px',
                  marginBottom: '8px',
                  transition: 'all 0.3s ease',
                  backgroundColor: item.article_url ? '#fafafa' : 'transparent'
                }}
                onClick={() => handleItemClick(item.article_url)}
                onMouseEnter={(e) => {
                  if (item.article_url) {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                    e.currentTarget.style.borderColor = '#d9d9d9';
                  }
                }}
                onMouseLeave={(e) => {
                  if (item.article_url) {
                    e.currentTarget.style.backgroundColor = '#fafafa';
                    e.currentTarget.style.borderColor = '#f0f0f0';
                  }
                }}
              >
                <div style={{ width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    {/* Date */}
                    <Text strong style={{ color: '#1890ff', fontSize: '13px' }}>
                      {item.date}
                    </Text>
                    
                    {/* Ticker */}
                    <Tag color="blue" style={{ fontSize: '12px', margin: '0' }}>
                      {item.ticker}
                    </Tag>
                    
                    {/* Impact Type with Icon */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {getImpactIcon(item.impact_type)}
                      <Text style={{ 
                        color: getImpactColor(item.impact_type), 
                        fontWeight: 'bold',
                        fontSize: '13px'
                      }}>
                        {formatImpactType(item.impact_type)}
                      </Text>
                    </div>
                    
                    {/* Sector/Industry */}
                    <Text style={{ fontSize: '13px', color: '#666' }}>
                      in {item.sector}/{item.industry}
                    </Text>
                    
                    {/* Metric */}
                    {item.metric_display && (
                      <Tag color="orange" style={{ fontSize: '11px', margin: '0' }}>
                        {item.metric_display}
                      </Tag>
                    )}
                    
                    {/* First 5 Words */}
                    <Tooltip title={item.full_description}>
                      <Text style={{ 
                        fontSize: '12px', 
                        color: '#888',
                        fontStyle: 'italic'
                      }}>
                        ({item.first_5_words}...)
                      </Text>
                    </Tooltip>
                    
                    {/* Timeline */}
                    <Text style={{ fontSize: '12px', color: '#666' }}>
                      by {item.timeline}
                    </Text>
                    
                    {/* External Link Icon */}
                    {item.article_url && (
                      <LinkOutlined 
                        style={{ 
                          color: '#1890ff', 
                          fontSize: '12px',
                          marginLeft: 'auto'
                        }} 
                      />
                    )}
                  </div>
                  
                  {/* Article Title (if available) */}
                  {item.article_title && (
                    <div style={{ marginTop: '4px' }}>
                      <Text 
                        style={{ 
                          fontSize: '11px', 
                          color: '#999',
                          fontStyle: 'italic'
                        }}
                      >
                        "{item.article_title}"
                      </Text>
                    </div>
                  )}
                </div>
              </List.Item>
            )}
          />
        )}
      </Spin>
    </div>
  );
};

export default MarketChangesAndTrends;
